/**
 * OMNI DECISION TWIN.
 *
 * Traditional bid leveling puts three quotes in a table and asks who is
 * cheapest. This asks a different question: WHAT PROJECT STATE EXISTS IF YOU
 * MAKE THIS DECISION?
 *
 * Each provider gets its own simulation, run deliberately, rendered on its own
 * terms. There is no winner, no ranking and no score — the backend compares a
 * scenario to the BASELINE and to nothing else, and two projections placed
 * side by side are two independent answers, not a leaderboard.
 *
 * Everything is HYPOTHETICAL and says so. The simulator writes nothing, and
 * the only action a projection may lead to is the existing, human-decided
 * award flow.
 */
import { useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  RuntimeStateBadge,
  HypotheticalMarker,
  MissingInformationCallout,
  ContractMismatchCallout,
} from "./RuntimePrimitives";
import {
  DECISION_TWIN_CONTRACT,
  presentQuoteForScenario,
  presentScenarioRefusal,
} from "./decisionTwinPresentation";
import { FlaskConical, ArrowRight, Info, TriangleAlert, RefreshCw } from "lucide-react";

const money = (amount, currency) =>
  typeof amount === "number"
    ? `${currency || ""} ${amount.toLocaleString()}`.trim()
    : "No amount stated";

/** Counts before and after, in the vocabulary's order. Never sorted by size. */
const CountShift = ({ before, after }) => {
  if (!before || !after) return null;
  const moved = before
    .map((b, i) => ({ ...b, after: after[i]?.count ?? 0 }))
    .filter((row) => row.count !== row.after);
  if (moved.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">
        No work front changes classification under this scenario.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid="count-shift">
      {moved.map((row) => (
        <span key={row.state} className="inline-flex items-center gap-1 text-[11px]">
          <RuntimeStateBadge state={row.state} />
          <span className="tabular-nums text-muted-foreground">{row.count}</span>
          <ArrowRight className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
          <span className="tabular-nums">{row.after}</span>
        </span>
      ))}
    </div>
  );
};

/**
 * One assumption, verbatim.
 *
 * A human hypothesis is visually distinct because it is a physical fact only
 * the site can establish — assumed here because a person asked, not because
 * anything verified it.
 */
const AssumptionRow = ({ assumption }) => (
  <div
    className={`rounded border p-2 text-[11px] min-w-0 ${
      assumption.provenanceDisplay.human
        ? "border-violet-500/40 bg-violet-500/10"
        : "border-border/50 bg-muted/10"
    }`}
    data-testid="assumption-row"
  >
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="font-medium">{assumption.kind}</span>
      <Badge variant="outline" className="text-[10px] font-normal">
        {assumption.provenanceDisplay.label}
      </Badge>
    </div>
    {assumption.provenanceDisplay.human && (
      <p className="text-[10px] text-violet-300 mt-0.5">{assumption.provenanceDisplay.meaning}</p>
    )}
    {assumption.changedNothing ? (
      <p className="text-[10px] text-muted-foreground mt-0.5">
        This restates what is already recorded, so it changed nothing.
      </p>
    ) : (
      assumption.changes.map((change) => (
        <p key={change.field} className="text-[10px] text-muted-foreground mt-0.5 break-words">
          {change.field}: {String(change.from ?? "not set")} → {String(change.to ?? "not set")}
        </p>
      ))
    )}
  </div>
);

/** One provider's projected future. */
const ScenarioPanel = ({ quote, scenario, error, loading, onRun }) => {
  if (loading) {
    return (
      <Card className="border-border/60">
        <CardContent className="p-3">
          <p className="text-sm font-medium">{quote.bidderDisplayName}</p>
          <p className="text-[12px] text-muted-foreground">Working through this future…</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const refusal = presentScenarioRefusal(error);
    return (
      <Card className="border-border/60" data-testid="scenario-refused">
        <CardContent className="p-3 space-y-1.5">
          <p className="text-sm font-medium">{quote.bidderDisplayName}</p>
          <p className="text-[12px] font-medium text-amber-400">{refusal.title}</p>
          <p className="text-[11px] text-muted-foreground">{refusal.detail}</p>
          {refusal.kind === "UNAVAILABLE" && (
            <Button variant="outline" size="sm" onClick={onRun}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Try again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!scenario) {
    return (
      <Card className="border-border/60" data-testid="scenario-idle">
        <CardContent className="p-3 space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium break-words">{quote.bidderDisplayName}</p>
              {/* A commercial fact, shown BESIDE the projection and never
                  fused into it. */}
              <p className="text-[11px] text-muted-foreground">
                {money(quote.baseAmount, quote.currency)}
                {quote.revision ? ` · revision ${quote.revision}` : ""}
              </p>
            </div>
            {quote.simulatable ? (
              <Button size="sm" variant="outline" onClick={onRun} data-testid="run-scenario">
                <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
                What if we choose this?
              </Button>
            ) : null}
          </div>
          {!quote.simulatable && (
            <p className="text-[11px] text-muted-foreground">{quote.notSimulatableReason}</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-violet-500/30 bg-violet-500/[0.03]"
      data-testid="scenario-result"
    >
      <CardContent className="p-3 space-y-2.5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-medium break-words min-w-0">{quote.bidderDisplayName}</p>
          <HypotheticalMarker appliedToCanonicalState={scenario.appliedToCanonicalState} />
        </div>

        {/* The contract promises this is false. If it is ever true, say so
            loudly rather than carrying on. */}
        {scenario.appliedToCanonicalState && (
          <p className="text-[11px] text-red-400 flex items-start gap-1">
            <TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            This response claims it was applied to the project. The simulator has no apply path —
            do not act on this until someone has checked it.
          </p>
        )}

        <ContractMismatchCallout
          received={scenario.contract.received}
          supported={DECISION_TWIN_CONTRACT}
        />

        <p className="text-[11px] text-muted-foreground">
          {money(quote.baseAmount, quote.currency)}
        </p>

        {/* Arithmetic, stated as arithmetic. */}
        <div className="space-y-1">
          <p className="text-[11px] font-medium">If this decision were made</p>
          <CountShift before={scenario.baselineCounts} after={scenario.projectedCounts} />
        </div>

        {scenario.changes.map((group) => (
          <div key={group.state} className="space-y-0.5">
            <p className="text-[11px] text-muted-foreground">
              Would be classified {group.label.toLowerCase()} ({group.changes.length})
            </p>
            {group.changes.map((change) => (
              <p key={change.instanceId} className="text-[11px] break-words">
                {change.instanceName || change.instanceId}
                {change.from.governingConstraint ? (
                  <span className="text-muted-foreground"> — was held by {change.from.governingConstraint}</span>
                ) : null}
              </p>
            ))}
          </div>
        ))}

        {scenario.schedule.finishMoved ? (
          <p className="text-[11px]">
            <span className="text-muted-foreground">Project finish: </span>
            {scenario.schedule.finishBefore} → {scenario.schedule.finishMoved}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground">The project finish does not move.</p>
        )}

        {/* The ledger, rendered rather than summarised. */}
        {scenario.assumptions.length > 0 && (
          <div className="space-y-1">
            <p className="text-[11px] font-medium">What was assumed</p>
            {scenario.assumptions.map((a, i) => (
              <AssumptionRow key={`${a.kind}-${i}`} assumption={a} />
            ))}
          </div>
        )}

        {/* A disclosure, not a footnote. */}
        {scenario.basis.heldAtPersisted.length > 0 && (
          <p className="text-[10px] text-muted-foreground border-l-2 border-border pl-2">
            Not recomputed for this scenario: {scenario.basis.heldAtPersisted.join(", ")}. A
            consequence OMNΛS cannot honestly compute is left out rather than estimated.
          </p>
        )}

        <MissingInformationCallout items={scenario.missingInformation} />

        {scenario.basis.sourceFingerprint && (
          <p className="text-[10px] text-muted-foreground font-mono break-all">
            basis {scenario.basis.sourceFingerprint.slice(0, 12)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const DecisionTwinPanel = ({
  quotes,
  scenarios,
  loadingQuoteId,
  errors,
  onRun,
  quotesLoading,
  quotesError,
}) => {
  const [showHow, setShowHow] = useState(false);

  if (quotesLoading) {
    return (
      <Card className="border-border/60">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">Reading the quotes on this package…</p>
        </CardContent>
      </Card>
    );
  }

  if (quotesError) {
    const refusal = presentScenarioRefusal(quotesError);
    return (
      <Card className="border-border/60" data-testid="twin-unavailable">
        <CardContent className="p-4 space-y-1.5">
          <p className="text-sm font-medium">{refusal.title}</p>
          <p className="text-[12px] text-muted-foreground">{refusal.detail}</p>
        </CardContent>
      </Card>
    );
  }

  const rows = (quotes || []).map(presentQuoteForScenario);

  if (rows.length === 0) {
    return (
      <Card className="border-border/60" data-testid="decision-twin">
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground">
            No quotes have been received for this package, so there is no decision to work
            through yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="decision-twin">
      <div className="space-y-1">
        <p className="text-sm">
          Rather than asking which quote is cheapest, ask what the project looks like if you
          choose it. Each future below is worked through separately and changes nothing.
        </p>
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline"
          onClick={() => setShowHow((v) => !v)}
          data-testid="twin-how"
        >
          {showHow ? "Hide" : "How OMNΛS works these out"}
        </button>
        {showHow && (
          <p className="text-[11px] text-muted-foreground flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
            Each scenario re-runs the same gates and schedule the live surfaces use, over a copy
            of the project. Nothing is written, and there is no way to apply one. OMNΛS does not
            rank these against each other — the decision is yours, through the award flow.
          </p>
        )}
      </div>

      {/* Ordered as the backend returned the quotes. Never sorted by amount,
          because sorting by amount is the leveling table this replaces. */}
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((quote) => (
          <ScenarioPanel
            key={quote.quoteId}
            quote={quote}
            scenario={scenarios?.[quote.quoteId] || null}
            error={errors?.[quote.quoteId] || null}
            loading={loadingQuoteId === quote.quoteId}
            onRun={() => onRun(quote)}
          />
        ))}
      </div>
    </div>
  );
};

export default DecisionTwinPanel;
